import config from '../config';
import { releaseZip } from './PublishService';
import { getHandoff } from './AppConfigService';
import { deliverySource } from './GitSourceService';
import { getProvenanceSummary, recordProvenanceEvent } from '../utils/vfsClient';
import { getBffHeaders } from '../utils/apiHeaders.js';

// Console Next (Mia-Platform Console v16) receives a Flow project as a delivery: the project's publishable files
// (the same zip as "Download", from vfs-service through the BFF, but as `release.zip`: refused with 409
// `quality_gate_blocked` while the Quality release gate blocks) plus its metadata. It registers a System with
// one Component per service, generates a Dockerfile and a Helm chart for each, and builds and deploys them.

// The browser never calls Console Next itself: its api-gateway takes no Flow-origin calls (no CORS, no cookie). The BFF
// forwards the delivery and the status query with the person's own token; `consoleNextUrl` only turns the card on and
// names the origin of the pages that are opened.
const viaBff = (path) => `${config.bffUrl}/api/mia-platform/console-next${path}`;

export const consoleNextEnabled = () => Boolean(config.consoleNextUrl);

/** The Console Next web page of a System. */
export function systemUrl(system) {
  const base = config.consoleNextWebUrl || config.consoleNextUrl;
  return `${base}/systems/${encodeURIComponent(system)}`;
}

/** A page of Console Next's web app from a path it answered (`/projects/shop/review?version=0.1.0`). */
export function consoleWebUrl(path) {
  const base = config.consoleNextWebUrl || config.consoleNextUrl;
  const clean = String(path || '');
  return `${base}${clean.startsWith('/') ? clean : `/${clean}`}`;
}

/**
 * Where each delivered Flow project stands in Console Next (`GET /api/deliveries/status?flowProject=a,b`), asked the
 * way the delivery is sent: from the browser, cross-origin. Resolves with the items (projects never delivered are left
 * out): `{ flowProject, system, title, version, state, label, detail, reason, failedStep, reviewPath, projectPath,
 * openPath, devUrl, devPath, stages: [{ stage, environment, version, status, since }] }`; `state` is `received`,
 * `review`, `building`, `ready`, `deploying`, `deployed` or `failed`. A connection failure rejects with an error whose
 * `unreachable` is true.
 */
export async function getDeliveryStatuses(projectIds) {
  const ids = [...new Set((projectIds || []).filter(Boolean))];
  if (!config.consoleNextUrl || ids.length === 0) { return []; }
  const url = viaBff(`/deliveries/status?flowProject=${ids.map(encodeURIComponent).join(',')}`);
  let response;
  try {
    response = await fetch(url, { headers: getBffHeaders({}, true), credentials: 'include' });
  } catch {
    // No answer at all (nothing listens there, DNS, CORS): `unreachable`, so the live poll stops asking.
    const error = new Error(`Console Next is not reachable at ${config.consoleNextUrl}.`);
    error.unreachable = true;
    throw error;
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error || `Console Next answered ${response.status}.`);
    // The BFF could not reach Console Next: the live poll stops asking, as when the browser could not.
    if (body.unreachable) { error.unreachable = true; }
    throw error;
  }
  return Array.isArray(body.items) ? body.items : [];
}

/**
 * The project's AI provenance as delivery metadata: labels and ids only (which tools, models, playbooks and keys were
 * used, and whether any of it is shadow AI) — never prompts, people or file contents. Null when nothing was recorded.
 */
export function provenanceForDelivery({ summary, shadow } = {}) {
  if (!summary) { return null; }
  const unique = (values) => [...new Set(values.filter(Boolean))];
  return {
    tools: unique((summary.tools || []).map((tool) => tool.label || tool.kind)),
    models: unique((summary.models || []).map((model) => (
      model.provider && model.provider !== 'unknown' ? `${model.provider}/${model.model}` : model.model
    ))),
    playbooks: unique((summary.playbooks || []).map((playbook) => playbook.id || playbook.name)),
    keySources: Object.entries(summary.keySources || {})
      .filter(([source, count]) => source !== 'none' && count > 0)
      .map(([source]) => source),
    shadowFlagged: Boolean(shadow?.flagged),
    shadowReasons: unique((shadow?.reasons || []).map((reason) => reason.label || reason.value)),
  };
}

// Best effort: a project without provenance (or a vfs-service that does not record it yet) still delivers.
async function deliveryProvenance(projectId) {
  try {
    return provenanceForDelivery(await getProvenanceSummary(projectId));
  } catch {
    return null;
  }
}

/**
 * The services' configuration as delivery metadata (`appConfig`, vfs-service src/appconfig/handoff.js): plain variables,
 * the secrets as items to provide in the target environment (a name and a suggested OpenBao key/property, never a value)
 * and the scheduled jobs as Kubernetes CronJobs. Null when there is none, or the sandbox is older (best effort).
 */
async function deliveryAppConfig(projectId) {
  try {
    const manifest = await getHandoff(projectId);
    return manifest && Object.keys(manifest.services || {}).length ? manifest : null;
  } catch {
    return null;
  }
}

// Where each service's code can be fetched from (Flow's git server, a short-lived read token, the connected repository
// when its mirror is current): GitSourceService. Best effort: without it the delivery is the zip, as before.
async function deliverySourceOf(projectId) {
  try {
    return await deliverySource(projectId);
  } catch {
    return { source: null, warnings: [] };
  }
}

/**
 * Sends the project to Console Next. Resolves with Console Next's answer:
 * `{ system, version, created, components, resources, apis, skipped, path }`, plus `flowReleaseId` when the release
 * zip named its release (X-Flow-Release-Id), and `sourceWarnings` when a connected repository could not be brought up to date.
 */
export async function sendToConsole({ projectId, name, description, services, requestedBy, flowUrl }) {
  if (!config.consoleNextUrl) {
    throw new Error('Console Next is not configured.');
  }
  // The release vfs-service served (Quality's build provenance), when its header is readable.
  let flowReleaseId = null;
  const [archive, provenance, appConfig, git] = await Promise.all([
    releaseZip(projectId, name, { onRelease: (id) => { flowReleaseId = id; } }), deliveryProvenance(projectId), deliveryAppConfig(projectId), deliverySourceOf(projectId),
  ]);
  const form = new FormData();
  form.append('metadata', JSON.stringify({
    projectId,
    name,
    description: description || undefined,
    services: services || [],
    requestedBy: requestedBy || undefined,
    flowUrl: flowUrl || undefined,
    provenance: provenance || undefined,
    appConfig: appConfig || undefined,
    source: git.source || undefined,
  }));
  form.append('archive', archive, `${String(name || 'project').replace(/[^a-zA-Z0-9-_]+/g, '-')}.zip`);
  let response;
  try {
    // No Content-Type: the browser sets the multipart boundary.
    response = await fetch(viaBff('/deliveries'), { method: 'POST', body: form, headers: getBffHeaders({}, true), credentials: 'include' });
  } catch (error) {
    throw new Error(`Console Next is not reachable at ${config.consoleNextUrl}.`);
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.error || `Console Next answered ${response.status}.`);
  }
  // Best effort: the publish goes into the project's AI provenance; a failure never fails the delivery.
  try {
    await recordProvenanceEvent(projectId, {
      action: 'publish',
      target: { deploy: { kind: 'console-next', system: body.system, version: body.version != null ? String(body.version) : undefined } },
    });
  } catch {
    // provenance is not critical
  }
  const answer = flowReleaseId ? { ...body, flowReleaseId } : body;
  return git.warnings?.length ? { ...answer, sourceWarnings: git.warnings } : answer;
}
